import {Box, Typography, useTheme} from '@mui/material';
import {CustomLink, ImportantButton} from '../common/button';
import {Display, Pack, Sale, Theme} from '../../types';
import {MaxHeight, Multiline} from '../common/text';
import {getDate, getToday, toMonthName} from '../../utils/date';

import {Colors} from '../common/colors';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';
import Image from 'next/image';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {ProStatus} from '../../types/models/ProStatus';
import {RATIO_IMAGE} from '../../utils/settings';
import ScheduleIcon from '@mui/icons-material/Schedule';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import {getLocaleLabels} from '../../utils/lang';
import {getTempPackTimeLeft} from '../../utils/campaign';
import useParser from '../../utils/htmlToReact';
import {useResponsive} from '../../provider/MediaQueryProvider';
import {useRouter} from 'next/router';
import {useTranslation} from 'next-i18next';
import {useUser} from '../../provider/UserProvider';

type PackElementProps = {
    theme?: Theme;
    pack: Pack;
    enabled: boolean;
    action?: () => void;
    limit: boolean;
    width: number;
    shadow: boolean;
    info: boolean;
};

export default function PackElement(props: PackElementProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode == 'dark';
    const {dbUser} = useUser();
    const router = useRouter();
    const {secret} = router.query;
    const {t, i18n} = useTranslation('common');
    const {isTablet, isMobile} = useResponsive();
    const parse = useParser();
    const pack = props.pack;
    // console.log('pack = ', pack);

    const label = getLocaleLabels(pack.labels, i18n.language);
    const picture = label.image_url;

    const unavailable = pack.stock_available == 0;
    const bestSeller = pack.display == Display.BEST_SELLER;
    const temp = pack.sale == Sale.TEMPORARY && pack.start_datetime && pack.end_datetime;
    const pro = pack.sale == Sale.PROFESSIONAL;
    const hidden = pack.sale == Sale.PRIVATE;

    let flash = false;
    let flashText = '';
    if (temp) {
        const today = getToday();
        const start = getDate(pack.start_datetime);
        const end = getDate(pack.end_datetime);
        // console.log('today = ', today);
        // console.log('start = ', start);
        // console.log('end = ', end);
        if (today >= start && today <= end) {
            flash = true;
            const timeLeft = getTempPackTimeLeft(pack);
            flashText = timeLeft.amount + ' ' + t(timeLeft.type, {count: timeLeft.amount});
        }
    }

    if (temp && !flash) {
        return <></>;
    }

    let displayProPack =
        pro &&
        Boolean(dbUser?.corporate_name) &&
        Boolean(dbUser.siret) &&
        dbUser.pro_status == ProStatus.VERIFIED;

    if (pro && !displayProPack) {
        return <></>;
    }

    let displayHiddenPack = secret && hidden && pack.secret_code == secret;

    if (hidden && !displayHiddenPack) {
        return <></>;
    }

    let delivery = '';
    if (pack.delivery_month && pack.delivery_year) {
        delivery = toMonthName(pack.delivery_month, i18n.language) + ' ' + pack.delivery_year;
    }

    // const BOX_SHADOW = 3;

    const overlay = displayHiddenPack || displayProPack || flash || bestSeller;

    const disabled = !props.enabled || pack.stock_available == 0;

    // console.log('disabled = ', disabled);
    const margin = isTablet || isMobile ? '5px' : '20px';

    const participation = pack.contribution ?? 0;
    const price = pack.price ?? 0;

    const className = `dangerous-html sun-editor-editable ${isDark ? 'dark' : 'light'}`;
    const cursorStyle = !isMobile && !disabled ? {cursor: 'pointer'} : {};
    const descriptionContent = label.description ? parse(label.description) : null;

    return (
        <Box
            onClick={disabled ? null : props.action}
            sx={{
                width: props.width,
                minWidth: props.width,
                maxWidth: props.width,
                // boxShadow: BOX_SHADOW,
                ...cursorStyle,
                borderRadius: '5px',
                // '&:hover': {boxShadow: disabled ? BOX_SHADOW : 12},
                margin: margin,
                WebkitBoxShadow: props.shadow ? Colors.shadow : 'none',
                boxShadow: props.shadow ? Colors.shadow : 'none',
            }}
            display="flex"
            flexDirection="column"
        >
            <Box style={{position: 'relative'}}>
                {Boolean(picture) && (
                    <Image
                        src={picture}
                        alt={label.name}
                        width={props.width}
                        height={Math.floor(props.width / RATIO_IMAGE)}
                        style={{
                            borderTopLeftRadius: '5px',
                            borderTopRightRadius: '5px',
                        }}
                    />
                )}
                {!Boolean(picture) && (
                    <HideImageOutlinedIcon
                        sx={{
                            width: props.width,
                            height: Math.floor(props.width / RATIO_IMAGE),
                            margin: 'auto',
                        }}
                    />
                )}
                {overlay && (
                    <OverlayTag
                        theme={props.theme}
                        bestSeller={bestSeller}
                        flash={flash}
                        flashText={flashText}
                        displayHiddenPack={displayHiddenPack}
                        displayProPack={displayProPack}
                    />
                )}
            </Box>
            <Box
                sx={{
                    padding: '5px',
                    borderBottomLeftRadius: '5px',
                    borderBottomRightRadius: '5px',
                    borderTop: '0px',
                    backgroundColor: isDark ? Colors.dark : 'none',
                }}
                display="flex"
                flex={1}
                flexDirection="column"
                justifyContent={'space-between'}
            >
                <Box display="flex" flexDirection="column">
                    <Multiline
                        numberOfLine={props.limit ? 2 : null}
                        typo={{sx: {fontSize: 18, fontWeight: 'bold', textAlign: 'center'}}}
                    >
                        <>{label.name}</>
                    </Multiline>
                    <Typography
                        sx={{
                            fontSize: 20,
                            fontWeight: 'bold',
                            textAlign: 'center',
                            color: props.theme?.dark_theme_background_color ?? Colors.second,
                        }}
                    >
                        {price} €
                    </Typography>
                    {pack.display_contribution && props.info && (
                        <Typography
                            sx={{
                                fontSize: 12,
                                textAlign: 'center',
                                margin: '10px',
                            }}
                        >
                            {participation}{' '}
                            {t('campaign.participations', {count: participation})}
                        </Typography>
                    )}
                    <MaxHeight
                        max={props.limit ? 150 : null}
                        typo={{sx: {}}}
                        className={className}
                    >
                        {descriptionContent}
                    </MaxHeight>
                </Box>
                <Box display="flex" flexDirection="column">
                    {props.limit && (
                        <CustomLink typo={{fontSize: 10}} onClick={props.action}>
                            {t('action.see_more')}
                        </CustomLink>
                    )}
                    {Boolean(props.action) && (
                        <ImportantButton
                            disabled={disabled}
                            onClick={props.action}
                            sx={{
                                marginTop: '10px',
                                backgroundColor: props.theme?.background_color ?? Colors.first,
                                '&:hover': {
                                    backgroundColor:
                                        props.theme?.hover_color ?? Colors.firstHover,
                                },
                            }}
                        >
                            {t('action.preorder')}
                        </ImportantButton>
                    )}
                    {pack.display_stock && props.info && (
                        <Typography
                            sx={{
                                fontSize: 12,
                                textAlign: 'center',
                                margin: '10px',
                            }}
                        >
                            {pack.stock_available == 0 && t('campaign.pack_none')}
                            {pack.stock_available > 0 && (
                                <>
                                    {pack.stock_available}{' '}
                                    {t('campaign.pack_left', {count: pack.stock_available})}
                                </>
                            )}
                        </Typography>
                    )}
                    {Boolean(delivery) && props.info && (
                        <Typography
                            sx={{
                                fontSize: 10,
                                textAlign: 'center',
                                margin: '10px',
                            }}
                        >
                            {t('label.delivery')} : {delivery}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

type OverlayProps = {
    title: string;
    icon: JSX.Element;
};

export function Overlay(props: OverlayProps) {
    return (
        <Box display="flex" flexDirection="row">
            {props.icon}
            <Typography
                sx={{
                    fontSize: 12,
                    alignSelf: 'center',
                    marginLeft: '4px',
                }}
            >
                {props.title}
            </Typography>
        </Box>
    );
}

type OverlayTagProps = {
    theme: Theme;
    bestSeller: boolean;
    flash: boolean;
    displayHiddenPack: boolean;
    displayProPack: boolean;
    flashText: string;
};

export function OverlayTag(props: OverlayTagProps) {
    return (
        <Box
            style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                display: 'block',
                margin: 'auto',
                textAlign: 'right',
            }}
        >
            <Box
                style={{
                    backgroundColor: props.theme?.background_color ?? Colors.first,
                    color: 'white',
                    marginTop: '-20px',
                    paddingRight: '4px',
                    paddingLeft: '4px',
                    paddingTop: '1px',
                    paddingBottom: '1px',
                    borderTopLeftRadius: '5px',
                    borderTopRightRadius: '5px',
                }}
            >
                <>
                    {props.bestSeller && (
                        <Overlay
                            title="Best Seller"
                            icon={<StarBorderIcon sx={{fontSize: '18px'}} />}
                        />
                    )}
                    {props.flash && (
                        <Overlay
                            title={props.flashText}
                            icon={<ScheduleIcon sx={{fontSize: '18px'}} />}
                        />
                    )}
                    {props.displayHiddenPack && (
                        <Overlay
                            title="Privé"
                            icon={<LockOpenIcon sx={{fontSize: '18px'}} />}
                        />
                    )}
                    {props.displayProPack && (
                        <Overlay
                            title="Pro"
                            icon={<WorkOutlineIcon sx={{fontSize: '18px'}} />}
                        />
                    )}
                </>
            </Box>
        </Box>
    );
}
